import {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { api } from "../lib/api";
import type { ReportOverview as ReportOverviewType } from "@hsh/shared";

export interface ReportOverviewRef {
  refresh: () => void;
}

const ReportOverview = forwardRef<ReportOverviewRef>(function ReportOverview(
  _,
  ref
) {
  const [report, setReport] = useState<ReportOverviewType | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [, setIsLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.getReportOverview();
      setReport(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Expose refresh method via ref
  useImperativeHandle(
    ref,
    () => ({
      refresh: fetchReport,
    }),
    [fetchReport]
  );

  if (!report) return null;

  return (
    <div className="absolute bottom-20 md:bottom-4 right-4 z-10 max-w-xs md:max-w-none">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Summary Bar */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 md:px-4 py-2 md:py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm">
            <div>
              <span className="font-semibold text-gray-900">
                {report.contactedLast7Days}
              </span>
              <span className="text-gray-500 ml-1 hidden md:inline">
                contacted this week
              </span>
              <span className="text-gray-500 ml-1 md:hidden">this week</span>
            </div>
            <div className="text-gray-300">|</div>
            <div>
              <span className="font-semibold text-green-600">
                {report.meetingsBooked}
              </span>
              <span className="text-gray-500 ml-1">meetings</span>
            </div>
          </div>
          <svg
            className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
        </button>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-3 md:p-4 space-y-3 md:space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm md:text-base">
              Outreach Overview
            </h3>

            {/* Status Breakdown */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              <div className="text-center p-1.5 md:p-2 bg-gray-50 rounded-lg">
                <div className="text-lg md:text-xl font-bold text-gray-900">
                  {report.byStatus.not_contacted}
                </div>
                <div className="text-xs text-gray-500">Not Contacted</div>
              </div>
              <div className="text-center p-1.5 md:p-2 bg-blue-50 rounded-lg">
                <div className="text-lg md:text-xl font-bold text-blue-600">
                  {report.byStatus.contacted}
                </div>
                <div className="text-xs text-gray-500">Contacted</div>
              </div>
              <div className="text-center p-1.5 md:p-2 bg-green-50 rounded-lg">
                <div className="text-lg md:text-xl font-bold text-green-600">
                  {report.byStatus.meeting_booked}
                </div>
                <div className="text-xs text-gray-500">Meetings</div>
              </div>
              <div className="text-center p-1.5 md:p-2 bg-purple-50 rounded-lg">
                <div className="text-lg md:text-xl font-bold text-purple-600">
                  {report.byStatus.referred}
                </div>
                <div className="text-xs text-gray-500">Referred</div>
              </div>
              <div className="text-center p-1.5 md:p-2 bg-yellow-50 rounded-lg">
                <div className="text-lg md:text-xl font-bold text-yellow-600">
                  {report.byStatus.follow_up}
                </div>
                <div className="text-xs text-gray-500">Follow Up</div>
              </div>
              <div className="text-center p-1.5 md:p-2 bg-red-50 rounded-lg">
                <div className="text-lg md:text-xl font-bold text-red-600">
                  {report.byStatus.not_suitable}
                </div>
                <div className="text-xs text-gray-500">Not Suitable</div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="text-xs md:text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Total places:</span>
                <span className="font-medium text-gray-900">
                  {report.totalPlaces}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Average score:</span>
                <span className="font-medium text-gray-900">
                  {report.averageScore}/100
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Contacted (30 days):</span>
                <span className="font-medium text-gray-900">
                  {report.contactedLast30Days}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ReportOverview;
